import React from 'react';

const About = () => {
    return (
        <div className="min-h-screen bg-white py-16 md:py-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#2C3333] mb-8 text-center uppercase tracking-tight">
                    About Us
                </h1>

                <div className="prose prose-lg max-w-none text-[#4B5563] space-y-8 leading-relaxed">

                    {/* Welcome */}
                    <section>
                        <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Welcome to HappyTring</h2>
                        <p>
                            HappyTring is a people-first platform where individuals can connect with trained mental health professionals in a safe, accessible, and structured environment.
                            We believe that taking the first step toward emotional wellbeing should never feel complicated. Our platform makes that step simple, supportive, and human.
                        </p>
                    </section>

                    {/* Who We Are */}
                    <section>
                        <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Who We Are</h2>
                        <p>
                            HappyTring is operated by Deeptrix Technologies Pvt. Ltd., based in Maharashtra, India.
                            We are a technology-driven company focused on building accessible digital platforms that solve real-world problems. With HappyTring, our mission is to bridge the gap between trained mental health professionals and individuals seeking emotional support.
                        </p>
                    </section>

                    {/* Leadership - Dr. Taapsi Malhotra */}
                    <section>
                        <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-6">Our Leadership</h2>
                        <p className="mb-6">
                            All trained mental health professionals on the HappyTring platform are overseen and guided by our Chief Head of Emotional Wellness &amp; Practitioner Network — <strong>Dr. Taapsi Malhotra</strong>. She heads every professional on our platform, ensuring the highest standard of care and ethical practice.
                        </p>

                        {/* Profile Card */}
                        <div className="bg-gradient-to-br from-[#F5F0E8] to-[#EDE8DE] rounded-3xl p-8 border border-[#D6C9B0] shadow-sm">
                            <div className="flex flex-col md:flex-row gap-8 items-start">

                                {/* Avatar Placeholder */}
                                <div className="flex-shrink-0">
                                    <div className="w-28 h-28 rounded-full bg-primary/20 flex items-center justify-center border-4 border-white shadow-md">
                                        <span className="font-heading text-4xl font-bold text-primary">TM</span>
                                    </div>
                                </div>

                                {/* Name & Title */}
                                <div className="flex-1">
                                    <h3 className="font-heading text-2xl font-bold text-[#2C3333] mb-1">Dr. Taapsi Malhotra</h3>
                                    <p className="text-primary font-semibold text-base mb-4">Chief Head of Emotional Wellness &amp; Practitioner Network</p>
                                    <p className="text-[#4B5563] leading-relaxed text-sm md:text-base">
                                        Dr. Taapsi Malhotra brings a rich academic background and a powerful portfolio of professional certifications to HappyTring. Her multi-disciplinary expertise — spanning psychology, neuro-linguistic programming, emotional intelligence, career coaching, and psychometric assessment — ensures that every professional on the platform is guided by a strong ethical and practical framework.
                                    </p>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="border-t border-[#D6C9B0] my-6"></div>

                            <div className="grid md:grid-cols-2 gap-6">

                                {/* Education */}
                                <div>
                                    <h4 className="font-heading text-lg font-semibold text-[#2C3333] mb-3 flex items-center gap-2">
                                        <span className="w-6 h-6 bg-primary/15 rounded-full flex items-center justify-center text-primary text-xs">🎓</span>
                                        Education
                                    </h4>
                                    <ul className="space-y-2 text-sm text-[#4B5563]">
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Honorary Doctorate (Honoris Causa) in Psychology</strong> — World Human Rights Protection Commission <span className="text-[#9CA3AF]">(February 2026)</span></span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>M.A. Psychology</strong> — Indira Gandhi National Open University (IGNOU)</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>B.A. (Hons) English</strong> — University of Delhi</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Senior Secondary (Commerce with Mathematics)</strong> — Manav Sthali School, Delhi</span>
                                        </li>
                                    </ul>
                                </div>

                                {/* Professional Certifications */}
                                <div>
                                    <h4 className="font-heading text-lg font-semibold text-[#2C3333] mb-3 flex items-center gap-2">
                                        <span className="w-6 h-6 bg-primary/15 rounded-full flex items-center justify-center text-primary text-xs">📜</span>
                                        Professional Certifications
                                    </h4>
                                    <ul className="space-y-2 text-sm text-[#4B5563]">
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>International Career Coach Program</strong> — Mindler</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Psychometric Assessment Certification</strong> — Counselors Council of India (CCI)</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Neuro Linguistic Programming (NLP)</strong> — Emocare</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Emotional Intelligence &amp; Life Coaching</strong> — Master My Life EQ Education Pvt. Ltd.</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-primary mt-1 flex-shrink-0">•</span>
                                            <span><strong>Certified Career Counselor</strong> — National Career Service Portal</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Vision & Mission */}
                    <section>
                        <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Our Vision &amp; Mission</h2>
                        <p>
                            We believe that mental health support should be <strong>accessible, transparent, easy to find</strong>, and less intimidating to approach. Our mission is to reduce friction in connecting with trained mental health professionals and support early emotional intervention by making meaningful support easily reachable.
                        </p>
                    </section>

                    {/* Transparency */}
                    <section>
                        <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Transparency &amp; Responsibility</h2>
                        <p>
                            We are committed to clear communication about our role as a connection platform and protecting user data in accordance with our Privacy Policy. While we strive to provide a useful and reliable experience, users are responsible for independently verifying credentials and making informed decisions.
                        </p>
                    </section>

                    {/* Contact */}
                    <div className="mt-16 p-8 bg-secondary/30 rounded-2xl border border-secondary">
                        <h3 className="font-heading text-xl font-semibold text-[#2C3333] mb-4">Contact Us</h3>
                        <div className="text-sm space-y-2">
                            <p className="font-medium text-primary">Deeptrix Technologies Pvt. Ltd.</p>
                            <p>502, Shree Sudarshan Building</p>
                            <p>Shree Avenue Complex</p>
                            <p>Mira Road – 401107, Maharashtra, India</p>
                            <p className="pt-2"><strong>Email:</strong> <a href="mailto:happytringdpl@gmail.com" className="text-primary hover:underline">happytringdpl@gmail.com</a></p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default About;
